import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Book, Envelope, LockKey, User as UserIcon, Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function formatErr(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return detail?.msg || String(detail);
}

export default function Signup() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [linked, setLinked] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, { email, password, name });
      if (data.email_verified) {
        setLinked(true);
        toast.success('Password linked to your existing account!');
      } else {
        setSent(true);
        toast.success('Verification email sent. Check your inbox!');
      }
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent || linked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-10 text-center shadow-sm" data-testid="signup-success">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-950/30 mb-6">
            <CheckCircle size={40} weight="duotone" className="text-green-600" />
          </div>
          <h2 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
            {linked ? 'Password linked!' : 'Check your email'}
          </h2>
          <p className="text-muted-foreground text-sm mb-6">
            {linked
              ? 'Your email/password login has been linked to your existing Google account. You can now sign in either way.'
              : (
                <>
                  We sent a verification link to <b className="text-foreground">{email}</b>. Click the link inside the email to activate your account.
                </>
              )}
          </p>
          <Link
            to="/login"
            data-testid="signup-goto-login"
            className="inline-block bg-primary text-primary-foreground rounded-full px-6 py-3 font-medium hover:opacity-90"
          >
            Go to login
          </Link>
          {!linked && (
            <p className="text-xs text-muted-foreground mt-4">
              Didn&apos;t get it? <button onClick={() => navigate('/forgot-password')} className="text-primary hover:underline">Resend verification</button>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg p-10 shadow-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Book size={32} weight="duotone" className="text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl tracking-tight mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Create account
            </h1>
            <p className="text-muted-foreground text-sm">Join a community of Hindi literature lovers</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
              <div className="relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  required
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name-input"
                  placeholder="Your name"
                  className="w-full pl-9 pr-3 py-3 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label>
              <div className="relative">
                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="signup-email-input"
                  placeholder="you@example.com"
                  className="w-full pl-9 pr-3 py-3 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Password</label>
              <div className="relative">
                <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="signup-password-input"
                  placeholder="At least 8 characters"
                  className="w-full pl-9 pr-10 py-3 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters</p>
            </div>

            {error && (
              <div data-testid="signup-error" className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="signup-submit"
              className="w-full bg-primary text-primary-foreground rounded-full py-3 font-semibold transition-transform hover:scale-[1.02] hover:opacity-90 disabled:opacity-60"
            >
              {busy ? 'Creating account…' : 'Create account'}
            </button>

            <p className="text-center text-sm text-muted-foreground pt-2">
              Already have an account?{' '}
              <Link to="/login" data-testid="signup-login-link" className="text-primary font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
